<template>
    <div class="flex mb-px draggable relative" :style="'background-color: '+item.color" :key="itemKey"
         @mouseover="highlight" @mouseleave="unhighlight">
        <!-- Transparent Cover on multimode -->
        <div class="absolute inset-0 -mt-px -mb-px z-20" v-show="this.multimode">
            <!-- Highlight if highlighted -->
            <div class="absolute inset-0 m-0 z-20 border-none bg-opacity-20 bg-red-300" v-show="this.selected"
                 @click="removeSelected">
            </div>
            <div class="absolute inset-0 m-0 z-20 border-none bg-opacity-10 bg-red-300 "
                 v-show="!this.selected && this.highlighted" @click="addSelected">
            </div>
        </div>

        <!-- Draggable handle. -->
        <div class="w-6 flex-shrink-0 py-1.5 -my-px border-t">
            <div class="w-5 h-5 flex items-center">
                <i class="fa fa-bars fa-fw text-xs text-gray-300 hover:text-gray-400 cursor-move pl-1 handle"
                   v-if="!estimate.readonly"></i>
            </div>
        </div>

        <!-- Reference. -->
        <div class="w-16 flex m-0 relative">
            <div
                class="flex flex-1 flex-col -ml-px -mt-px mr-0 -mb-px border border-gray-200 shadow-none hover:z-10"
                :style="'background-color: '+item.color"
                :class="estimate.readonly?'':'hover:border-gray-400'">
                <page-cell
                    class="flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none min-h-[34px]"
                    :style="'background-color: '+item.color"
                    :placeholder="item.defaultReference"
                    :read-only="estimate.readonly"
                    v-model="item.reference"
                    @input="estimate.update()"
                    @change="estimate.addSnapshot()"
                    ref="reference"
                ></page-cell>
            </div>
        </div>

        <!-- Item -->
        <div class="flex-1">
            <!-- Main Item -->
            <div class="flex">
                <!-- Description. -->
                <div class="flex-1 flex m-0 relative">
                    <div
                        :key="renderKey"
                        class="flex flex-1 flex-col m-0 -ml-px -mt-px mr-0 -mb-px border border-gray-200 shadow-none"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400 hover:z-10'">
                        <template v-if="estimate.readonly">
                            <div
                                class="text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative whitespace-pre-wrap focus:border-blue-500 border-none focus:z-20 resize-none"
                                :style="'background-color: '+item.color+';border-color: '+item.color"
                                v-text="item.description">
                            </div>
                        </template>
                        <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'"
                                                ref="autoresizable-description" v-else
                        >
                        <textarea
                            placeholder="Description..."
                            class="text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            type="text"
                            ref="description"
                            :disabled="estimate.readonly"
                            v-model="item.description"
                            @input="updateEstimate()"
                            @focus=""
                            @blur="rebuildCollectedItems(estimate)"
                            @change="changeEstimate()"
                            rows="1"
                        ></textarea>
                        </autoresizable-textarea>
                    </div>
                </div>

                <!-- Quantity. -->
                <div class="w-20 m-0 flex">
                    <div
                        class="flex flex-1 flex-col -ml-px -mt-px mr-0 -mb-px border border-gray-200 shadow-none hover:z-10"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400'">
                        <page-cell
                            class="flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none min-h-[34px]"
                            :style="'background-color: '+item.color"
                            placeholder="Qty"
                            :read-only="estimate.readonly"
                            v-model="quantity"
                            @input="updateQuantity"
                            @blur="updateAll()"
                            @change="updateAll();estimate.addSnapshot()"
                        ></page-cell>
                    </div>
                </div>

                <!-- Units of measure. -->
                <div class="w-20 m-0 flex">
                    <div
                        class="flex flex-1 flex-col -ml-px -mt-px mr-0 -mb-px border border-gray-200 shadow-none hover:z-10"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400'">
                        <page-cell
                            class="flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none min-h-[34px]"
                            :style="'background-color: '+item.color"
                            placeholder="Uom"
                            :read-only="estimate.readonly"
                            v-model="item.uom"
                            @input="estimate.update()"
                            @change="estimate.addSnapshot()"
                        ></page-cell>
                    </div>
                </div>

                <!-- Rate. -->
                <div class="w-28 m-0 flex">
                    <div
                        class="flex flex-1 flex-col -ml-px -mt-px mr-0 -mb-px border border-gray-200 shadow-none hover:z-10"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400'">
                        <page-cell
                            currency
                            class="text-right flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none min-h-[34px]"
                            :placeholder="item.quantity?(item.total/item.quantity):0 | toCurrency()"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            :read-only="estimate.readonly"
                            v-model="rate"
                            @input="updateRate"
                            @blur="updateAll()"
                            @change="updateAll();estimate.addSnapshot()"
                        ></page-cell>
                    </div>
                </div>
                <!-- Total. -->
                <div class="w-28 m-0 flex">
                    <div
                        class="flex flex-1 flex-col -ml-px -mt-px mr-0 -mb-px border border-gray-200 shadow-none hover:z-10"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400'">
                        <page-cell
                            currency
                            class="text-right flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none min-h-[34px]"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            :placeholder="item.valueWithDefaultOHP | toCurrency()"
                            :read-only="estimate.readonly"
                            v-model="total"
                            @input="updateTotal"
                            @blur="updateAll()"
                            @change="updateAll();estimate.addSnapshot()"
                        ></page-cell>
                    </div>
                </div>
                <!-- Dropdown. -->
                <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1">
                    <ui-dropdown menu-class="p-3 w-56 z-50" :enabled="inView"
                                 :key="clipboardKey"
                                 :close-on-mouse-out-scroll="true">
                        <template #default="{ toggle }">
                            <button @click="toggle"
                                    class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                                <i class="fa fa-ellipsis-h"></i>
                            </button>
                        </template>
                        <template #menu="{ close }">
                            <ui-dropdown-item icon="fas fa-tint"
                                              @mouseleave="(e) => {$refs['itemcolormenu'].close(e)}"
                                              @mouseenter="openColorSubmenu"
                                              ref="itemcolor"
                                              :submenu="true"
                                              v-if="!estimate.readonly"
                            >
                                Set item color
                                <div class="fixed" style="width:auto;height:auto;overflow: visible"
                                     :ref="'itemcolorrelative'">
                                    <vue-context :ref="'itemcolormenu'" :autoFocus="false"
                                                 class="inline pb-0 focus:outline-none focus:ring-0 z-30"
                                                 :close-on-click="false" :close-on-scroll="false">
                                        <div :id="'itemcolormenu'"
                                             class="p-3 w-48 z-30 bg-white rounded-lg border border-gray-300 shadow-lg">
                                            <div
                                                v-for="(index,key) in ([0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19])"
                                                class="w-8 inline-block items-center p-1.5 border border-none rounded-lg"
                                                :key="index">
                                                <button
                                                    class="w-6 h-6 rounded-lg flex-none mt-0.5 mr-2 inline-block border border-gray-200 hover:border-gray-400"
                                                    :style="'background-color: '+color(key)"
                                                    @mousedown="setColor(item,color(key))">
                                                </button>
                                            </div>
                                        </div>
                                    </vue-context>
                                </div>
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item icon="fas fa-cut" @mousedown="cutItem();close();"
                                              v-if="!estimate.readonly">Cut Item
                            </ui-dropdown-item>
                            <ui-dropdown-item icon="fas fa-copy" @mousedown="copyItem();close();">Copy Item
                            </ui-dropdown-item>
                            <ui-dropdown-item icon="fas fa-paste"
                                              v-if="!estimate.readonly && (clipboardGroupItemData[0])"
                                              @mousedown="pasteItem(clipboardGroupItemData[0],item);close();">
                                Paste Item
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item ref="paste1item"
                                              @mouseleave="closePasteSubMenu(1)"
                                              @mouseenter="openPasteSubMenu(1)"
                                              v-if="!estimate.readonly"
                                              icon="fas fa-clipboard"
                                              :submenu="true"
                            >Library
                                <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                     :ref="'paste1relative'">
                                    <vue-context :ref="'paste1menu'" :autoFocus="false"
                                                 class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                                 :close-on-click="false">
                                        <div :id="'paste1menu'"
                                             class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg">
                                            <ui-dropdown-item icon="fas fa-plus"
                                                              @mousedown="addToLibrary(item);close();">
                                                Add Preliminary to Library
                                            </ui-dropdown-item>
                                            <ui-dropdown-divider
                                                v-if="!estimate.readonly && (clipboardGroupItemData.length > 1)"></ui-dropdown-divider>
                                            <div class="max-h-96 overflow-x-auto">
                                                <ui-dropdown-item icon="fas fa-paste"
                                                                  button-class="truncate"
                                                                  class="w-full truncate flex"
                                                                  v-for="(tem,i) in clipboardGroupItemData"
                                                                  v-if="i > 0"
                                                                  :key="'paste1-'+i"
                                                                  @mousedown="pasteItem(tem.data,item);close();">
                                                    <div class="text-xs text-blue-500 truncate">{{ tem.name }}</div>
                                                </ui-dropdown-item>
                                            </div>
                                        </div>
                                    </vue-context>
                                </div>
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item v-if="!estimate.readonly" icon="fas fa-trash" danger
                                              @mousedown="deleteItem();close();">Delete
                            </ui-dropdown-item>
                        </template>
                    </ui-dropdown>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import draggable from 'vuedraggable'
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import {cloneDeep, isNull} from "lodash";
import {resolveNumericText, toCurrency} from "../../../utils";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import UiDropdown from "../../ui/UiDropdown.vue";
import PageCell from "../../ui/PageCell.vue";
import * as math from 'mathjs';
import remoteStorage from "../../../utils/remoteStorage";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import {Estimate} from "@models";

export default {
    components: {
        UiDropdownDivider,
        PageCell, UiDropdown, draggable, AutoresizableTextarea, UiDropdownItem
    },
    props: {
        estimate: Object,
        group: Object,
        item: Object,
        store: Object,
        multimode: false,
        clipboardKey: Number,
        clipboardGroupItemData: Array,
    },
    data() {
        return {
            quantity: this.item.quantity ? this.item.quantity : '',
            total: this.item.total,
            rate: this.item.rate,
            editing: false,
            resolveNumericText,
            itemKey: 0,
            renderKey: 0,
            newItem: {
                description: null,
            },
            searcherOpen: false,
            highlighted: false,
            selected: false,
            distance: {top: 0, bottom: 0},
            inView: this.item.isNew,
        }
    },
    watch: {
        item: {
            handler(newValue, oldValue) {
                if (this.editing !== 'quantity') {
                    this.quantity = this.item.quantity ? this.item.quantity : ''
                }
                if (this.editing !== 'total') {
                    this.total = this.item.total
                }
                if (this.editing !== 'rate') {
                    this.rate = math.round((this.item.quantity ? (this.item.total / this.item.quantity) : 0), 2)
                }
            },
            deep: true,
        },
        "estimate.collectionBuildUUID": {
            handler(newValue, oldValue) {
                this.renderKey = this.renderKey + 1
            },
            deep: true,
        }
    },
    methods: {
        removeSelected() {
            this.selected = false;
        },
        addSelected() {
            this.selected = true;
        },
        highlight() {
            if (this.multimode) {
                this.highlighted = true
            }
        },
        unhighlight() {
            if (this.multimode) {
                this.highlighted = false
            }
        },
        addToLibrary(item) {
            this.$modals.open(
                'estimate-add-item-to-library',
                {
                    name: (item.description + ' - ' + item.reference + ' (' + toCurrency(item.total) + ')').replace(/(\r\n|\n|\r)/gm, ""),
                    title: `Add Preliminary Item to Library`,
                    button: 'Add Preliminary Item',
                    useGroups: false,
                    handle: (name, group) => {
                        this.clipboardGroupItemData.push({
                            name: name,
                            data: cloneDeep(item)
                        })
                        remoteStorage.setItem('estimate-group-item-clipboard', JSON.stringify(this.clipboardGroupItemData))
                        remoteStorage.setItem('estimate-clipboard-view', 'groupitem')
                    }
                }
            )
        },
        closePasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].close()
            } else {
                this.$refs[p].close()
            }
        },
        openPasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].open(new window.MouseEvent('mouse'))
            } else {
                this.$refs[p].open(new window.MouseEvent('mouse'))
            }
            this.positionPasteMenu(num)
        },
        pasteItem(item, at) {
            remoteStorage.setItem('estimate-clipboard-view', 'groupitem');
            let num = this.group.items.indexOf(at);
            this.group.items.splice(num, 0, cloneDeep(item));
            this.estimate.update();
            this.estimate.addSnapshot();
            this.$emit('reflow')
        },
        positionPasteMenu(num) {
            this.$nextTick(() => {
                if (this.$refs["paste" + num + "relative"][0]) {
                    this.$refs["paste" + num + "relative"][0].setAttribute('style', '')
                } else {
                    this.$refs["paste" + num + "relative"].setAttribute('style', '')
                }
                this.$nextTick(() => {
                    let opener, box
                    if (this.$refs["paste" + num + "item"][0]) {
                        opener = this.$refs["paste" + num + "item"][0].$refs.submenu.getBoundingClientRect()
                    } else {
                        opener = this.$refs["paste" + num + "item"].$refs.submenu.getBoundingClientRect()
                    }
                    if (this.$refs["paste" + num + "menu"][0]) {
                        box = this.$refs["paste" + num + "menu"][0].$el.getBoundingClientRect()
                    } else {
                        box = this.$refs["paste" + num + "menu"].$el.getBoundingClientRect()
                    }
                    let left = Math.min(opener.x - box.x - 10, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 10, window.innerHeight - (box.bottom) - 28);
                    if (this.$refs["paste" + num + "relative"][0]) {
                        this.$refs["paste" + num + "relative"][0].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    } else {
                        this.$refs["paste" + num + "relative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    }
                })
            })
        },
        color(x) {
            return this.estimate.colors(x);
        },
        setColor(item, color) {
            item.color = color
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        openColorSubmenu(e) {
            this.$refs["itemcolormenu"].open(new window.MouseEvent('mouse'))
            this.$nextTick(() => {
                this.$refs["itemcolorrelative"].setAttribute('style', '')
                this.$nextTick(() => {
                    const opener = this.$refs.itemcolor.$refs.submenu.getBoundingClientRect()
                    const box = this.$refs["itemcolor"].$el.getBoundingClientRect()
                    let left = Math.min(opener.x - box.x - 40, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 16, window.innerHeight - (box.bottom) - 28);
                    this.$refs["itemcolorrelative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                })
            })
        },
        isNull,
        round(a, b) {
            if (a) {
                return math.round(a, 0 + b);
            }
            return 0;
        },
        isNumeric(num) {
            return (typeof (num) === 'number' || typeof (num) === "string" && num.trim() !== '') && !isNaN(num);
        },
        cutItem() {
            this.clipboardGroupItemData[0] = cloneDeep(this.item)
            remoteStorage.setItem('estimate-group-item-clipboard', JSON.stringify(this.clipboardGroupItemData))
            remoteStorage.setItem('estimate-clipboard-view', 'groupitem');
            this.group.items = this.group.items.filter((item) => item !== this.item)
            this.estimate.update()
            this.estimate.addSnapshot()
            this.$emit('reflow')
        },
        copyItem() {
            this.clipboardGroupItemData[0] = cloneDeep(this.item)
            remoteStorage.setItem('estimate-group-item-clipboard', JSON.stringify(this.clipboardGroupItemData))
            remoteStorage.setItem('estimate-clipboard-view', 'groupitem');
        },
        deleteItem() {
            this.group.items = this.group.items.filter((item) => item !== this.item)
            this.estimate.update()
            this.estimate.addSnapshot()
            this.$emit('reflow')
        },
        updateTotal(val) {
            this.editing = 'total'
            this.item.total = math.round(this.resolveNumericText(val), 2);
            if (this.item.quantity) {
                this.item.rate = math.round(this.item.total / this.item.quantity, 2);
            }
            this.estimate.update()
        },
        updateRate(val) {
            this.editing = 'rate'
            this.item.rate = math.round(this.resolveNumericText(val), 2);
            if (this.item.quantity) {
                this.item.total = math.round(this.item.rate * this.item.quantity, 2);
            }
            this.estimate.update()
        },
        updateQuantity() {
            this.editing = 'quantity'
            this.item.quantity = math.round(this.resolveNumericText(this.quantity), 3);
            this.item.total = math.round(this.item.rate * this.item.quantity, 2);
            this.estimate.updateReferences(); // Build it up, to find correct totals
            this.estimate.update()
        },
        updateAll(val) {
            this.editing = false
            this.quantity = this.item.quantity ? this.item.quantity : ''
            this.total = this.item.total
            this.rate = math.round(this.item.rate, 2)
            this.total = math.round(this.item.total, 2)
        },
        getItemColor() {
            return this.estimate.collectedItems.items.has(Estimate.itemAsKey(this.item)) ? this.estimate.collectedItems.items.get(Estimate.itemAsKey(this.item)).color : ''
        },
        setIsInView(val) {
            if (this.inView !== val) {
                this.inView = val
                return true
            }
            return false
        },
        changeEstimate() {
            let loc = document.getElementById('ui-crud-main-body').scrollTop;
            this.estimate.addSnapshot()
            document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            this.$nextTick(() => {
                document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            })
        },
        updateEstimate() {
            let loc = document.getElementById('ui-crud-main-body').scrollTop;
            this.estimate.update()
            document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            this.$nextTick(() => {
                document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            })
        },
        rebuildCollectedItems() {
            let loc = document.getElementById('ui-crud-main-body').scrollTop;
            this.estimate.buildCollectedItems();
            document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            this.$nextTick(() => {
                document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            })
        }
    },
    mounted() {
    },
}
</script>
